import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { 
  Zap, 
  LayoutDashboard, 
  Briefcase, 
  Users, 
  Link2, 
  FileBarChart,
  CreditCard,
  LogOut,
  Menu,
  ShieldCheck,
  Sun,
  Moon
} from "lucide-react";
import { useState, useEffect } from "react";
import { LanguageSelector } from "./LanguageSelector";
import { UniversalSearch } from "./UniversalSearch";
import { useTheme } from "../contexts/ThemeContext";

const API = process.env.REACT_APP_BACKEND_URL;

export function Navbar({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [reportsCount, setReportsCount] = useState(0);

  // Fetch reports count for notification badge
  useEffect(() => {
    const fetchReportsCount = async () => {
      try {
        const lastViewed = localStorage.getItem('ema_reports_last_viewed') || '';
        const url = lastViewed 
          ? `${API}/api/reports/count?since=${encodeURIComponent(lastViewed)}`
          : `${API}/api/reports/count`;
        const response = await fetch(url, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setReportsCount(data.count || 0);
        }
      } catch (error) {
        console.error('Error fetching reports count:', error);
      }
    };

    if (user) {
      fetchReportsCount();
      // Refresh count every 30 seconds
      const interval = setInterval(fetchReportsCount, 30000);
      // Listen for reports viewed event to update badge immediately
      const handleReportsViewed = () => fetchReportsCount();
      window.addEventListener('ema_reports_viewed', handleReportsViewed);
      return () => {
        clearInterval(interval);
        window.removeEventListener('ema_reports_viewed', handleReportsViewed);
      };
    }
  }, [user]);

  const navItems = [
    { path: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
    { path: '/jobs', label: t('nav.jobs'), icon: Briefcase },
    { path: '/candidates', label: t('nav.candidates'), icon: Users },
    { path: '/interview-links', label: t('nav.links'), icon: Link2 },
    { path: '/reports', label: t('nav.reports'), icon: FileBarChart, badge: reportsCount },
    { path: '/billing', label: t('nav.billing'), icon: CreditCard },
  ];

  const handleLogout = async () => {
    try {
      await fetch(`${API}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    navigate('/');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <nav className="sticky-header px-4 sm:px-6 py-2 sm:py-3">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo */}
        <div 
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => navigate('/dashboard')}
          data-testid="nav-logo"
        >
          <img 
            src={require('../assets/ema-logo.png')} 
            alt="Ema - by Proprio Technologies" 
            className="h-10 sm:h-14 w-auto"
          />
        </div>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Button
                key={item.path}
                variant="ghost"
                size="sm"
                className={`nav-link px-4 py-2 relative ${isActive ? 'active text-brand-primary bg-brand-accent/10' : 'text-ink-muted dark:text-zinc-400 hover:text-ink dark:hover:text-zinc-100'}`}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {item.label}
                {item.badge > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </Button>
            );
          })}
        </div>

        {/* Search Bar */}
        <div className="hidden md:block">
          <UniversalSearch />
        </div>

        {/* User Menu */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="h-9 w-9 text-ink-muted hover:text-ink dark:text-zinc-400 dark:hover:text-zinc-100"
            data-testid="theme-toggle-btn"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          
          <LanguageSelector variant="ghost" />
          
          {user?.interview_credits !== undefined && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-brand-accent/20 rounded-full">
              <span className="text-sm font-medium text-brand-primary dark:text-brand-accent">
                {user.interview_credits} {t('nav.credits')}
              </span>
            </div>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-trigger">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-brand-primary text-brand-accent font-medium">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <div className="flex items-center gap-3 p-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-brand-primary text-brand-accent">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <p className="text-sm font-medium text-ink">{user?.name}</p>
                  <p className="text-xs text-ink-muted">{user?.email}</p>
                </div>
              </div>
              
              {/* Super Admin / Admin Link */}
              {(user?.role === 'super_admin' || user?.role === 'admin_employee') && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => navigate('/admin')}
                    className="cursor-pointer bg-indigo-50 text-indigo-700 hover:bg-indigo-100 focus:bg-indigo-100"
                  >
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Console Admin
                  </DropdownMenuItem>
                </>
              )}

              <DropdownMenuSeparator />
              <div className="lg:hidden">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className="cursor-pointer"
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {item.label}
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
              </div>
              <DropdownMenuItem 
                onClick={handleLogout}
                className="cursor-pointer text-destructive focus:text-destructive"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('nav.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <Menu className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-zinc-200 dark:border-zinc-700 mt-3 pt-3">
          <div className="flex flex-col gap-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Button
                  key={item.path}
                  variant="ghost"
                  className={`justify-start ${isActive ? 'text-brand-primary bg-brand-accent/10' : 'text-ink-muted'}`}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {item.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
}
